import React from "react";

export interface UserStore {
  retail_store_num: any;
}

export interface NotAssociatedUser {
  id: number;
  email: string;
  stores: UserStore[];
}

interface UsersNotFoundModalProps {
  isOpen: boolean;
  users: any[];
  onYes: () => void;
  onNo: () => void;
  onClose: () => void;
}

const UsersNotFoundModal: React.FC<UsersNotFoundModalProps> = ({
  isOpen,
  users,
  onYes,
  onNo,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Inject responsive styles */}
      <style>{`
        .unf-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
          box-sizing: border-box;
        }

        .unf-modal {
          background-color: #fff;
          border-radius: 12px;
          padding: 32px;
          max-width: 1100px;
          width: 100%;
          position: relative;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
        }

        .unf-close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: #000;
          line-height: 1;
          padding: 4px;
        }

        .unf-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
          padding-right: 32px;
        }

        .unf-icon {
          width: 48px;
          height: 48px;
          min-width: 48px;
          background-color: #F5A623;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .unf-title {
          font-size: 24px;
          font-weight: 700;
          color: #000;
          margin: 0;
        }

        .unf-message {
          font-size: 16px;
          color: #666;
          margin-bottom: 24px;
          line-height: 1.5;
        }

        .unf-emails-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
          max-height: 400px;
          overflow-y: auto;
          padding: 4px;
        }

        .unf-email-box {
          border: 2px solid #F5A623;
          border-radius: 8px;
          padding: 14px 18px;
          font-size: 14px;
          color: #333;
          background-color: #fff;
          word-break: break-word;
        }

        .unf-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: wrap;
        }

        .unf-btn {
          padding: 12px 32px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          border: 2px solid #F5A623;
          transition: all 0.2s ease;
          min-width: 100px;
        }

        .unf-btn-yes {
          background-color: #F5A623;
          color: #fff;
        }

        .unf-btn-no {
          background-color: #fff;
          color: #000;
        }

        /* ── Tablet ── */
        @media (max-width: 768px) {
          .unf-modal {
            padding: 24px;
            max-height: 85vh;
          }
          .unf-title {
            font-size: 20px;
          }
          .unf-message {
            font-size: 14px;
          }
          .unf-emails-grid {
            grid-template-columns: 1fr 1fr;
            max-height: 300px;
          }
        }

        /* ── Mobile ── */
        @media (max-width: 480px) {
          .unf-modal {
            padding: 16px;
            border-radius: 8px;
            max-height: 80vh;
          }
          .unf-close-btn {
            top: 12px;
            right: 12px;
            font-size: 24px;
          }
          .unf-header {
            gap: 12px;
            margin-bottom: 16px;
          }
          .unf-icon {
            width: 36px;
            height: 36px;
            min-width: 36px;
          }
          .unf-icon svg {
            width: 18px;
            height: 18px;
          }
          .unf-title {
            font-size: 18px;
          }
          .unf-message {
            font-size: 13px;
            margin-bottom: 16px;
          }
          .unf-emails-grid {
            grid-template-columns: 1fr;
            gap: 10px;
            margin-bottom: 20px;
            max-height: 220px;
          }
          .unf-email-box {
            padding: 10px 14px;
            font-size: 13px;
          }
          .unf-buttons {
            justify-content: stretch;
          }
          .unf-btn {
            flex: 1;
            padding: 10px 16px;
            font-size: 14px;
            text-align: center;
          }
        }
      `}</style>

      <div className="unf-overlay" onClick={onClose}>
        <div className="unf-modal" onClick={(e) => e.stopPropagation()}>
          <button className="unf-close-btn" onClick={onClose}>×</button>

          <div className="unf-header">
            <div className="unf-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L2 7L12 12L22 7L12 2Z"
                  fill="white" stroke="white" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"
                />
                <path
                  d="M2 17L12 22L22 17"
                  stroke="white" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"
                />
                <path
                  d="M2 12L12 17L22 12"
                  stroke="white" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="unf-title">Users Not Found</h2>
          </div>

          <p className="unf-message">
            The following users are not part of the chain. Would you like to add them to the chain
            and assign a role?
          </p>

          <div className="unf-emails-grid">
            {users.map((user, index) => (
              <div key={index} className="unf-email-box">
                {user?.email}
              </div>
            ))}
          </div>

          <div className="unf-buttons">
            <button className="unf-btn unf-btn-yes" onClick={onYes}>Yes</button>
            <button className="unf-btn unf-btn-no" onClick={onNo}>No</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersNotFoundModal;